import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Lead } from '../lead.model';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LeadsService } from 'src/app/service/lead.service';
import { HttpService } from 'src/app/https.service';
import { URLUtils } from 'src/app/urlUtils';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-lead-details-modal',
    templateUrl: './lead-details-modal.component.html',
    styleUrls: ['./lead-details-modal.component.css'],
    standalone: false
})

export class LeadDetailsModalComponent {
  leadId: string = '';
  lead: any = {};
  editMode: boolean = false;
  @Output() leadUpdated = new EventEmitter<void>();
  leadForm!: FormGroup;
  uid:any;
  specializations = [
    'Criminal Law', 
    'Civil Law', 
    'Corporate Law', 
    'Family Law', 
    'Immigration Law', 
    'IP Law',
    'Tax Law',
    'Real Estate Law',
    'Banking Law',
    'Other'
  ];

  countries: string[] = [
    "Afghanistan", "Albania", "Algeria", /* ... other countries ... */ "Zimbabwe"
  ];

  constructor(
    private route: ActivatedRoute,
    private httpService: HttpService,
    private spinner: NgxSpinnerService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.leadId = params['id'];
      this.getLeadDetails();
    });

    this.initializeForm();
  }

  getLeadDetails() {
    this.spinner.show();
    this.httpService.sendGetRequest(URLUtils.updateLeads(this.leadId))
      .subscribe({
        next: (response: any) => {
          this.spinner.hide();
          this.lead = response.data;
          this.patchFormValues();
        },
        error: (error) => {
          this.spinner.hide();
          this.toastr.error('Failed to load lead details');
        }
      });
  }

  initializeForm() {
    this.leadForm = this.fb.group({
      fullname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      contact_phone: ['', Validators.pattern('^[0-9]{10}$')],
      status: [''],
      category: ['', Validators.required],
      subcategory: ['', Validators.required],
      tin: ['', Validators.required],
      designation: [''],
      no_of_lawyers: [0],
      additional_category: [''],
      notes: [''],
      address: this.fb.group({
        address_type: [''],
        house_flat_no: [''],
        street: [''],
        landmark: [''],
        city_town: [''],
        state: [''],
        country: [''],
        zipcode: ['']
      })
    });
  }

  patchFormValues() {
    if (this.lead) {
      this.leadForm.patchValue({
        fullname: this.lead.fullname || '',
        email: this.lead.email || '',
        contact_phone: this.lead.contact_phone || '',
        status: this.lead.status || '',
        category: this.lead.category || '',
        subcategory: this.lead.subcategory && this.lead.subcategory.length > 0 ? this.lead.subcategory[0] : '',
        tin: this.lead.tin || '',
        designation: this.lead.designation || '',
        no_of_lawyers: this.lead.no_of_lawyers || 0,
        additional_category: this.lead.additional_category || '',
        notes: this.lead.notes || '',
        address: {
          address_type: this.lead.address?.address_type || '',
          house_flat_no: this.lead.address?.house_flat_no || '',
          street: this.lead.address?.street || '',
          landmark: this.lead.address?.landmark || '',
          city_town: this.lead.address?.city_town || '',
          state: this.lead.address?.state || '',
          country: this.lead.address?.country || '',
          zipcode: this.lead.address?.zipcode || ''
        }
      });
    }
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.patchFormValues(); // Reset form if cancel editing
    }
  }

  saveChanges() {
    if (this.leadForm.invalid) {
      this.markFormGroupTouched(this.leadForm);
      return;
    }

    const formValues = this.leadForm.value;
    this.uid = localStorage.getItem('profuid');
    const payload = {
      ...formValues,
      subcategory: formValues.subcategory ? [formValues.subcategory] : [],
      uid: this.uid
    };

    this.spinner.show();
    this.httpService.sendPutRequest(URLUtils.updateLeads(this.leadId), payload)
      .subscribe({
        next: (response: any) => {
          this.spinner.hide();
          this.toastr.success('Lead updated successfully');
          this.editMode = false;
          this.getLeadDetails(); // Refresh data
          this.leadUpdated.emit(); // Notify parent component
        },
        error: (error) => {
          this.spinner.hide();
          this.toastr.error('Failed to update lead');
        }
      });
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  get isEntity() {
    return this.lead.category === 'entity';
  }

  get isLawyer() {
    return this.lead.category === 'solo';
  }
}
